<script setup lang="ts">
	import { computed, nextTick, ref, watch } from 'vue';

	type Props = {
		length?: number;
		label?: string;
		errorMessage?: string;
		hasError?: boolean;
		inputmode?: 'numeric' | 'text';
	};

	const props = withDefaults(defineProps<Props>(), {
		length: 8,
		label: '',
		errorMessage: '',
		inputmode: 'numeric',
		hasError: false,
	});

	const code = defineModel<string>({ required: true });

	const emit = defineEmits<{
		submit: [];
	}>();

	const inputs = ref<(HTMLInputElement | null)[]>([]);
	const characters = computed<string[]>(() => {
		const out = Array(props.length).fill('');
		const chars = (code.value || '').split('');
		for (let i = 0; i < props.length && i < chars.length; i++) out[i] = chars[i];
		return out;
	});

	const setChars = (next: string[]) => {
		code.value = next.join('').slice(0, props.length);
	};

	const focusIndex = (i: number) => {
		const target = inputs.value[i];
		if (!target) return;
		target.focus();
		target.select();
	};

	const onInput = (i: number, event: Event) => {
		const target = event.target as HTMLInputElement;
		const raw = target.value;
		// User typed or pasted into one box
		const incoming = raw.slice(0, props.length - i).split('');
		const next = [...characters.value];
		for (let j = 0; j < incoming.length; j++) next[i + j] = incoming[j];
		// If shorter than before (deletion), trim trailing chars from this index
		if (incoming.length === 0) next[i] = '';
		setChars(next);
		nextTick(() => {
			const advance = Math.min(i + (incoming.length || 0), props.length - 1);
			focusIndex(incoming.length === 0 ? i : advance);
		});
	};

	const onKeydown = (i: number, event: KeyboardEvent) => {
		const isEmpty = !characters.value[i];
		if (event.key === 'Enter') {
			event.preventDefault();
			emit('submit');
			return;
		}
		if (event.key === 'Backspace') {
			if (isEmpty && i > 0) {
				event.preventDefault();
				const next = [...characters.value];
				next[i - 1] = '';
				setChars(next);
				nextTick(() => focusIndex(i - 1));
			}
			return;
		}
		if (event.key === 'ArrowLeft' && i > 0) {
			event.preventDefault();
			focusIndex(i - 1);
		} else if (event.key === 'ArrowRight' && i < props.length - 1) {
			event.preventDefault();
			focusIndex(i + 1);
		}
	};

	const onPaste = (i: number, event: ClipboardEvent) => {
		event.preventDefault();
		const pasted = event.clipboardData?.getData('text') ?? '';
		const incoming = pasted
			.replace(/\s/g, '')
			.slice(0, props.length - i)
			.split('');
		if (!incoming.length) return;
		const next = [...characters.value];
		for (let j = 0; j < incoming.length; j++) next[i + j] = incoming[j];
		setChars(next);
		nextTick(() => focusIndex(Math.min(i + incoming.length, props.length - 1)));
	};

	const onFocus = (event: FocusEvent) => {
		(event.target as HTMLInputElement).select();
	};

	watch(
		() => code.value,
		(val) => {
			if ((val ?? '').length === 0) nextTick(() => focusIndex(0));
		},
	);

	defineExpose({ focus: () => focusIndex(0) });
</script>

<template>
	<div class="flex flex-col gap-2 w-full">
		<label v-if="label" class="text-base font-bold text-text-default">{{ label }}</label>
		<div class="flex gap-1 md:gap-2 w-full">
			<input
				v-for="i in props.length"
				:key="i"
				:ref="
					(el) => {
						inputs[i - 1] = el as HTMLInputElement;
					}
				"
				:value="characters[i - 1]"
				size="1"
				type="text"
				autocomplete="one-time-code"
				:inputmode="inputmode"
				maxlength="1"
				class="flex-1 basis-0 min-w-0 max-w-12 h-16 md:h-20 rounded-md border text-center text-xl md:text-2xl font-bold text-text-default outline-none transition-colors"
				:class="
					hasError
						? 'bg-bg-form-error border-border-form-error focus:border-border-form-error'
						: characters[i - 1]
							? 'bg-bg-form-default border-border-form-filled focus:border-border-form-active'
							: 'bg-bg-form-default border-border-form-default focus:border-border-form-active'
				"
				@input="onInput(i - 1, $event)"
				@keydown="onKeydown(i - 1, $event)"
				@paste="onPaste(i - 1, $event)"
				@focus="onFocus"
			/>
		</div>
		<p v-if="hasError && errorMessage" class="text-sm text-icon-error">{{ errorMessage }}</p>
	</div>
</template>
